import { useCallback, useEffect, useState } from "react";
import { api } from "@/services/api";
import type { Payment, Programme } from "@/types/api";

interface UsePaymentsReturn {
  payments: Payment[];
  programmes: Map<string, Programme>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage payment history
 */
export function usePayments(): UsePaymentsReturn {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [programmes, setProgrammes] = useState<Map<string, Programme>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const paymentData = await api.getMyPayments();
      setPayments(paymentData);

      // Fetch programme details
      const uniqueProgrammeIds = [...new Set(paymentData.map((p) => p.programme_id))];
      const programmeMap = new Map<string, Programme>();

      await Promise.all(
        uniqueProgrammeIds.map(async (id) => {
          try {
            const programme = await api.getProgramme(id);
            programmeMap.set(id, programme);
          } catch (err) {
            console.error(`Failed to fetch programme ${id}:`, err);
          }
        })
      );

      setProgrammes(programmeMap);
    } catch (err) {
      console.error("Failed to fetch payment history:", err);
      setError(err instanceof Error ? err.message : "Failed to load payment history");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  return {
    payments,
    programmes,
    isLoading,
    error,
    refetch: fetchPayments,
  };
}
